import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSafeBack } from "../hooks/useSafeBack";
import axios from "axios";
import {
  ArrowLeft, RefreshCw, Users, GraduationCap, Gauge, Sparkles,
  TrendingUp, AlertTriangle, CheckCircle2, EyeOff, Clock, Flame,
  Loader2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_META = {
  learner: { label: "Learner", icon: Users, color: "from-blue-500 to-blue-600" },
  adi: { label: "Instructor", icon: GraduationCap, color: "from-emerald-500 to-emerald-600" },
  instructor: { label: "Instructor", icon: GraduationCap, color: "from-emerald-500 to-emerald-600" },
  pdi: { label: "PDI", icon: Gauge, color: "from-violet-500 to-violet-600" },
};

const ACTION_EVENTS = ["card_click", "upgrade_click", "booking_start", "booking_complete", "upload_start"];

export default function AdminFunnel() {
  const nav = useNavigate();
  const goBack = useSafeBack();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data: r } = await axios.get(`${API}/api/metrics/funnel`, {
        withCredentials: true,
      });
      setData(r);
      setError("");
    } catch (err) {
      setError(err.response?.status === 403 ? "Admin access only" : "Unable to load funnel data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onRefresh = () => {
    setRefreshing(true);
    load();
  };

  if (loading) {
    return (
      <div className="min-h-dvh no-overflow-x flex items-center justify-center bg-slate-50 dark:bg-[#0c1120]">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] flex flex-col items-center justify-center text-center p-6">
        <AlertTriangle className="w-10 h-10 text-amber-500 mb-3" />
        <p className="text-base font-bold text-slate-900 dark:text-white">{error || "No data"}</p>
        <button
          data-testid="admin-funnel-back"
          onClick={() => goBack()}
          className="mt-4 text-sm font-bold text-indigo-600 hover:underline"
        >
          Go back
        </button>
      </div>
    );
  }

  const today = data.today_per_role || {};
  const trend = data.trend_7d || [];
  const opp = data.opportunities || {};
  const topByRole = data.top_by_role_7d || {};
  const upgradeByRole = data.upgrade_by_role_7d || {};
  const ttfa = data.time_to_first_action_seconds || { p50: 0, p90: 0, samples: 0 };

  const learnerToday = sumActions(today.learner);
  const instructorToday = sumActions(today.adi) + sumActions(today.instructor);
  const pdiToday = sumActions(today.pdi);

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-24" data-testid="admin-funnel-page">
      {/* Header */}
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center gap-3">
          <button
            data-testid="admin-funnel-back-btn"
            onClick={() => goBack()}
            className="text-slate-400 hover:text-slate-600"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <p className="font-extrabold text-slate-900 dark:text-white text-sm flex-1">
            Funnel Command Centre
          </p>
          <button
            data-testid="admin-funnel-refresh"
            onClick={onRefresh}
            disabled={refreshing}
            className="text-slate-400 hover:text-slate-600 p-1"
            aria-label="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 pt-4 space-y-4">
        {/* Per-role today cards */}
        <section className="grid grid-cols-3 gap-3" data-testid="today-role-cards">
          <RoleTodayCard role="learner" value={learnerToday} />
          <RoleTodayCard role="adi" value={instructorToday} />
          <RoleTodayCard role="pdi" value={pdiToday} />
        </section>

        {/* 7-day trend strip */}
        <TrendChart trend={trend} />

        {/* Top clicked by role + upgrade clicks */}
        <div className="grid md:grid-cols-2 gap-4">
          <RoleTopCard
            title="Top actions by role"
            testId="top-by-role-card"
            roleMap={topByRole}
            emptyLabel="No click data yet"
          />
          <RoleTopCard
            title="Upgrade clicks by role"
            testId="upgrade-by-role-card"
            roleMap={upgradeByRole}
            emptyLabel="No upgrade clicks yet"
            accent="amber"
          />
        </div>

        {/* Time to first action + drop-off */}
        <div className="grid md:grid-cols-2 gap-4">
          <StatBlock
            testId="ttfa-card"
            icon={<Clock className="w-5 h-5 text-indigo-500" />}
            title="Time to first action"
            lines={[
              { label: "p50", value: humanDuration(ttfa.p50), emph: true },
              { label: "p90", value: humanDuration(ttfa.p90) },
              { label: "samples", value: ttfa.samples },
            ]}
          />
          <StatBlock
            testId="dropoff-card"
            icon={<EyeOff className="w-5 h-5 text-rose-500" />}
            title="Drop-off alert (7d)"
            lines={[
              { label: "Users with page views + no clicks", value: data.dropoff_users_7d, emph: true },
            ]}
            accent={data.dropoff_users_7d > 0 ? "rose" : null}
            hint={data.dropoff_users_7d > 0
              ? `${data.dropoff_users_7d} users saw pages but didn't act. Review copy/CTAs.`
              : "Every active user engaged with at least one card."}
          />
        </div>

        {/* Opportunities */}
        <section
          data-testid="opportunities-card"
          className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 space-y-3"
        >
          <p className="text-xs uppercase tracking-wider font-bold text-slate-500">Opportunities (7-day)</p>
          <OppRow
            testId="opp-ignored"
            title="Ignored cards"
            icon={<EyeOff className="w-4 h-4 text-slate-400" />}
            tone="slate"
            items={opp.ignored_cards || []}
            empty="All known cards have at least 1 click — nice."
            render={(x) => <code className="text-[11px] font-mono">{x}</code>}
          />
          <OppRow
            testId="opp-dead"
            title="Dead funnels (page views without any click today)"
            icon={<AlertTriangle className="w-4 h-4 text-rose-500" />}
            tone="rose"
            items={opp.dead_funnels || []}
            empty="No dead funnels today."
            render={(x) => <span>{ROLE_META[x.role]?.label || x.role} · {x.page_views} views</span>}
          />
          <OppRow
            testId="opp-strong"
            title="Strong upgrade converters"
            icon={<Flame className="w-4 h-4 text-amber-500" />}
            tone="emerald"
            items={opp.strong_converters || []}
            empty="No clear converters yet — needs more traffic."
            render={(x) => (
              <span>
                <code className="text-[11px] font-mono">{x.testid}</code> · {x.upgrade_clicks} clicks · {x.rate_pct}%
              </span>
            )}
          />
        </section>
      </main>
    </div>
  );
}

// ── Small building blocks ───────────────────────────────────────────

function sumActions(eventsMap) {
  if (!eventsMap) return 0;
  return ACTION_EVENTS.reduce((a, e) => a + (eventsMap[e] || 0), 0);
}

function RoleTodayCard({ role, value }) {
  const meta = ROLE_META[role] || ROLE_META.learner;
  const Icon = meta.icon;
  return (
    <div
      data-testid={`role-card-${role}`}
      className={`rounded-2xl bg-gradient-to-br ${meta.color} text-white p-4`}
    >
      <div className="flex items-center gap-2 text-[11px] uppercase font-bold opacity-80">
        <Icon className="w-3.5 h-3.5" />
        {meta.label} today
      </div>
      <p className="text-3xl font-extrabold leading-tight mt-1">{value}</p>
      <p className="text-[11px] opacity-80 mt-0.5">actions</p>
    </div>
  );
}

function TrendChart({ trend }) {
  if (!trend.length) {
    return (
      <div
        data-testid="trend-chart-empty"
        className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 text-sm text-slate-400 text-center"
      >
        No 7-day data yet
      </div>
    );
  }
  const max = Math.max(...trend.map((d) => d.total), 1);
  return (
    <div
      data-testid="trend-chart"
      className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4"
    >
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="w-4 h-4 text-indigo-500" />
        <p className="text-xs uppercase tracking-wider font-bold text-slate-500">Last 7 days</p>
      </div>
      <div className="flex items-end gap-1.5 h-24">
        {trend.map((d) => {
          const pct = Math.round((d.total / max) * 100);
          const label = new Date(d.date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short" });
          return (
            <div
              key={d.date}
              data-testid={`trend-bar-${d.date}`}
              className="flex-1 flex flex-col items-center gap-1 group"
            >
              <div className="flex-1 flex items-end w-full">
                <div
                  className="w-full rounded-t-md bg-indigo-500 group-hover:bg-indigo-600 transition-colors"
                  style={{ height: `${pct}%`, minHeight: d.total > 0 ? "4px" : "0" }}
                  title={`${d.total} events`}
                />
              </div>
              <span className="text-[10px] text-slate-400 font-bold">{label}</span>
              <span className="text-[9px] text-slate-500">{d.total}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RoleTopCard({ title, roleMap, testId, emptyLabel, accent }) {
  const roles = Object.keys(roleMap).filter((r) => r !== "anonymous" && r !== "unknown");
  return (
    <div
      data-testid={testId}
      className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4"
    >
      <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">{title}</p>
      {roles.length === 0 ? (
        <p className="text-sm text-slate-400 py-4 text-center">{emptyLabel}</p>
      ) : (
        <div className="space-y-4">
          {roles.map((r) => {
            const rows = roleMap[r] || [];
            if (!rows.length) return null;
            const meta = ROLE_META[r] || ROLE_META.learner;
            return (
              <div key={r} data-testid={`top-${r}-block`}>
                <p className="text-[11px] font-extrabold text-slate-700 dark:text-slate-200 mb-1.5 uppercase">
                  {meta.label}
                </p>
                <div className="space-y-1">
                  {rows.slice(0, 5).map((row, i) => (
                    <div
                      key={`${r}-${row.testid}-${i}`}
                      data-testid={`top-row-${r}-${i}`}
                      className="flex items-center justify-between text-xs"
                    >
                      <code className="font-mono text-slate-600 dark:text-slate-300 truncate pr-2">{row.testid}</code>
                      <span className={`font-bold flex-shrink-0 ${accent === "amber" ? "text-amber-600" : "text-slate-900 dark:text-white"}`}>
                        {row.clicks}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatBlock({ testId, icon, title, lines, accent, hint }) {
  const borderClass = accent === "rose"
    ? "border-rose-200 dark:border-rose-900/40 bg-rose-50 dark:bg-rose-900/10"
    : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900";
  return (
    <div data-testid={testId} className={`rounded-2xl border p-4 ${borderClass}`}>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <p className="text-xs uppercase tracking-wider font-bold text-slate-500">{title}</p>
      </div>
      <div className="space-y-1">
        {lines.map((l) => (
          <div key={l.label} className="flex items-center justify-between">
            <span className="text-[11px] text-slate-500 uppercase">{l.label}</span>
            <span className={`font-extrabold ${l.emph ? "text-xl text-slate-900 dark:text-white" : "text-sm text-slate-700 dark:text-slate-300"}`}>
              {l.value}
            </span>
          </div>
        ))}
      </div>
      {hint && <p className="text-[11px] text-slate-500 mt-2 leading-snug">{hint}</p>}
    </div>
  );
}

function OppRow({ testId, title, icon, items, empty, render }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-2 mb-1.5">
        {icon}
        <p className="text-[11px] font-bold text-slate-700 dark:text-slate-200 uppercase">{title}</p>
      </div>
      {items.length === 0 ? (
        <p className="text-[11px] text-slate-400 italic flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3 text-emerald-400" /> {empty}
        </p>
      ) : (
        <ul className="space-y-0.5 text-xs text-slate-600 dark:text-slate-400">
          {items.slice(0, 10).map((x, i) => (
            <li key={i} className="pl-5 list-disc list-inside">{render(x)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function humanDuration(seconds) {
  if (!seconds || seconds <= 0) return "–";
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h`;
  return `${Math.round(seconds / 86400)}d`;
}
